package org.example.project.game

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

class SinglePlayerViewModel(private val langCode: String, private val difficulty: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private val repository = LevelRepository()

    private val _state = MutableStateFlow<GameState?>(null)
    val state = _state.asStateFlow()

    private val _isLoading = MutableStateFlow(true)
    val isLoading = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage = _errorMessage.asStateFlow()

    // NEU: Eigene StateFlows, damit wir das streikende 'GameModels' umgehen!
    private val _isSurrendered = MutableStateFlow(false)
    val isSurrendered = _isSurrendered.asStateFlow()

    private val _isFinalHinted = MutableStateFlow(false)
    val isFinalHinted = _isFinalHinted.asStateFlow()

    init { loadNewLevel() }

    fun loadNewLevel() {
        scope.launch {
            _isLoading.value = true
            _errorMessage.value = null
            _isSurrendered.value = false
            _isFinalHinted.value = false
            try {
                val (_, pack) = repository.fetchRandomLevel(langCode, difficulty)
                _state.value = GameState(columns = pack.toColumns(), finalSolution = pack.finalSolution)
                _isLoading.value = false
            } catch (t: Throwable) {
                _errorMessage.value = t.message
                _isLoading.value = false
            }
        }
    }

    fun surrender() {
        val current = _state.value ?: return
        if (current.isGameWon || _isSurrendered.value) return
        val newColumns = current.columns.map { col ->
            col.copy(isSolved = true, fields = col.fields.map { f -> f.copy(isRevealed = true) })
        }
        _state.value = current.copy(columns = newColumns)
        _isSurrendered.value = true
    }

    fun useThunder(columnId: String) {
        val current = _state.value ?: return
        if (current.isGameWon || _isSurrendered.value) return
        val newColumns = current.columns.map { col ->
            if (col.id == columnId && !col.isSolved) col.copy(isHinted = true) else col
        }
        _state.value = current.copy(columns = newColumns)
    }

    fun useFinalHint() {
        val current = _state.value ?: return
        if (current.isGameWon || _isSurrendered.value || _isFinalHinted.value) return
        _isFinalHinted.value = true
    }

    fun revealHint(columnId: String, fieldId: String) {
        val current = _state.value ?: return
        if (current.isGameWon || _isSurrendered.value) return
        val column = current.columns.find { it.id == columnId } ?: return
        val field = column.fields.find { it.id == fieldId } ?: return
        if (field.isRevealed || column.isSolved) return
        val newFields = column.fields.map { if (it.id == fieldId) it.copy(isRevealed = true) else it }
        val newColumns = current.columns.map { if (it.id == columnId) column.copy(fields = newFields) else it }
        _state.value = current.copy(columns = newColumns, currentScore = current.currentScore - 5)
    }

    fun guessColumn(columnId: String, guess: String): Boolean {
        val current = _state.value ?: return false
        val column = current.columns.find { it.id == columnId } ?: return false
        if (column.columnSolution.equals(guess.trim(), ignoreCase = true)) {
            val newColumns = current.columns.map {
                if (it.id == columnId) it.copy(isSolved = true, fields = it.fields.map { f -> f.copy(isRevealed = true) }) else it
            }
            _state.value = current.copy(columns = newColumns, currentScore = current.currentScore + 20)
            return true
        }
        return false
    }

    fun guessFinal(guess: String): Boolean {
        val current = _state.value ?: return false
        if (current.finalSolution.equals(guess.trim(), ignoreCase = true)) {
            val newCols = current.columns.map { it.copy(isSolved = true, fields = it.fields.map { f -> f.copy(isRevealed = true) }) }
            _state.value = current.copy(columns = newCols, isGameWon = true, currentScore = current.currentScore + 50)
            return true
        }
        return false
    }

    fun dispose() { scope.cancel() }
}